import React, { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import {
  Form,
  Segment,
  Grid,
  Icon,
  Divider,
  Header,
  Button,
  Modal,
  Message,
  Loader,
  Popup,
  Checkbox,
} from "semantic-ui-react";
import networkSelectors from "../../../selectors/network";
import { postContact } from "../../../queries/contacts";
import useApiCallWithToast from "../../../hooks/useApiCallWithToast";
import { ContactInfoOptions } from "../../permits/types/ContactInfoOptions";
import { usePreventEnterOnInputElements } from "../../../hooks/usePreventEnterOnInputElements";
import { postPersonSearch } from "../../../queries/search";
import { GQLBuilder } from "../../../utils/GQLBuilder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBriefcase,
  faEnvelope,
  faIdBadge,
  faLocationDot,
  faMailboxFlagUp,
  faPhone,
} from "@fortawesome/pro-regular-svg-icons";
import { parsedPhoneNumberAsString } from "../../../components/global_components/form_components/ControlledPhoneNumber";
import { ContactFieldsV2 } from "./ContactFieldsV2";
import { buildNewContactPayload } from "../utils/buildContactPayload";
import {
  CreatePermitContactFormData,
  personESToPermitContactFormData,
  personRubyToPermitContactFormData,
} from "../../permits/utils/buildPermitsContactPayload";
import { PersonBodyES } from "../types/Person";

interface IContactSearchModal {
  onCreateContact: (newContactId: string, permitContactFormData: CreatePermitContactFormData) => any; // function to run when new contact is created
  onSelectContact: (contactId: string, permitContactFormData: CreatePermitContactFormData) => any; // function to run when existing contact is selected
  setModalClosed: () => any; // function to run when modal is closed
  open: boolean; // whether or not the modal is open
  headerText?: string;
  subHeaderText?: string;
  createContactButtonText?: string;
  selectContactButtonText?: string;
  contactInfoOptions?: ContactInfoOptions;
  closeModalOnCreate?: boolean;
  closeModalOnSelect?: boolean;
  isCreatePermitContactLoading?: boolean;
  isSelectContactLoading?: boolean;
  applicationType?: string;
  aliasPhysicalAddressAsHome?: boolean;
}

function canSearchWithFormValues({
  firstName,
  lastName,
  email,
  phone,
  address,
  address2,
  city,
  state,
  zip,
  physical_address,
  physical_address2,
  physical_city,
  physical_state,
  physical_zip,
  companyName,
  licenseNumber,
}: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  physical_address: string;
  physical_address2: string;
  physical_city: string;
  physical_state: string;
  physical_zip: string;
  companyName: string;
  licenseNumber: string;
}) {
  // Check if any are longer than three since that is the minum length to match ngram tokens length in the backend
  const allSufficientlyLong =
    firstName?.length >= 3 ||
    lastName?.length >= 3 ||
    email?.length >= 3 ||
    phone?.length >= 3 ||
    address?.length >= 3 ||
    address2?.length >= 3 ||
    city?.length >= 3 ||
    state?.length >= 2 || // Expect for state, which is two characters
    zip?.length >= 3 ||
    physical_address?.length >= 3 ||
    physical_address2?.length >= 3 ||
    physical_city?.length >= 3 ||
    physical_state?.length >= 2 || // Expect for state, which is two characters
    physical_zip?.length >= 3 ||
    companyName?.length >= 3 ||
    licenseNumber?.length >= 3;

  return allSufficientlyLong;
}

function emptyStringIfShorterThan(value: string, shorterThan: number) {
  return value?.length >= shorterThan ? value : "";
}

export const ContactSearchModal = (props: IContactSearchModal) => {
  const {
    onCreateContact,
    onSelectContact,
    setModalClosed,
    open,
    headerText = "Create Contact",
    subHeaderText = "",
    createContactButtonText = "Create Contact",
    selectContactButtonText = "Select",
    contactInfoOptions = {
      address: "hidden",
      company_name: "hidden",
      email_address: "required",
      license_number: "hidden",
      phone_number: "optional",
    },
    closeModalOnCreate = true,
    closeModalOnSelect = true,
    isCreatePermitContactLoading = false,
    isSelectContactLoading = false,
    applicationType,
  } = props;
  const networkHeaders = useSelector(networkSelectors.defaultHeaders);
  const [selectedApplicantId, setSelectedApplicantId] = useState<number>(); // selected applicant from search results
  const formContext = useForm();

  usePreventEnterOnInputElements();

  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const [searchPersons] = useMutation(postPersonSearch(networkHeaders), {
    onSettled: () => {
      setSearchLoading(false);
    },
    onSuccess: () => {
      setSearchError(false);
    },
    onError: () => {
      setSearchError(true);
    },
  });

  // Search for citizens that match search text
  const getCitizens = async ({
    firstName,
    lastName,
    email,
    phone,
    address,
    address2,
    city,
    state,
    zip,
    physical_address,
    physical_address2,
    physical_city,
    physical_state,
    physical_zip,
    companyName,
    licenseNumber,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    physical_address: string;
    physical_address2: string;
    physical_city: string;
    physical_state: string;
    physical_zip: string;
    companyName: string;
    licenseNumber: string;
  }) => {
    setSearchLoading(true);
    setSearchError(false);

    const gql = new GQLBuilder()
      .size(25)
      .query((query) => {
        query
          .or((or) => {
            or.field("firstName", "contains", emptyStringIfShorterThan(firstName, 3))
              .field("lastName", "contains", emptyStringIfShorterThan(lastName, 3))
              .field("email", "contains", emptyStringIfShorterThan(email, 3))
              .field("phone", "contains", emptyStringIfShorterThan(phone, 3))
              .field("address", "contains", emptyStringIfShorterThan(address, 3))
              .field("address2", "contains", emptyStringIfShorterThan(address2, 3))
              .field("city", "contains", emptyStringIfShorterThan(city, 3))
              .field("state", "contains", emptyStringIfShorterThan(state, 2))
              .field("zip", "contains", emptyStringIfShorterThan(zip, 3))
              .field("physical_address", "contains", emptyStringIfShorterThan(physical_address, 3))
              .field("physical_address2", "contains", emptyStringIfShorterThan(physical_address2, 3))
              .field("physical_city", "contains", emptyStringIfShorterThan(physical_city, 3))
              .field("physical_state", "contains", emptyStringIfShorterThan(physical_state, 2))
              .field("physical_zip", "contains", emptyStringIfShorterThan(physical_zip, 3))
              .field("companyName", "contains", emptyStringIfShorterThan(companyName, 3))
              .field("licenseNumber", "contains", emptyStringIfShorterThan(licenseNumber, 3));
          })
          .and((and) => {
            and.field("personType", "equal", "citizen");
          });
      })
      .fieldSettings((fieldSettings) => {
        fieldSettings
          .field("firstName", { weight: 1 })
          .field("lastName", { weight: 2 })
          .field("email", { weight: 5 })
          .field("phone", { weight: 5 })
          .field("address", { weight: 4 })
          .field("address2", { weight: 1 })
          .field("city", { weight: 3 })
          .field("state", { weight: 3 })
          .field("zip", { weight: 3 })
          .field("physical_address", { weight: 4 })
          .field("physical_address2", { weight: 1 })
          .field("physical_city", { weight: 3 })
          .field("physical_state", { weight: 3 })
          .field("physical_zip", { weight: 3 })
          .field("companyName", { weight: 4 })
          .field("licenseNumber", { weight: 5 });
      })
      .build();

    const response = await searchPersons(gql);
    const accounts = response?.hits?.hits?.map((hit: { _source: any }) => hit._source) ?? [];

    setAccounts(accounts);
  };

  const firstName = formContext.watch("firstName");
  const lastName = formContext.watch("lastName");
  const email = formContext.watch("email");
  const phone = formContext.watch("phone");
  const address = formContext.watch("address");
  const address2 = formContext.watch("address2");
  const city = formContext.watch("city");
  const state = formContext.watch("state");
  const zip = formContext.watch("zip");
  const physical_address = formContext.watch("physical_address");
  const physical_address2 = formContext.watch("physical_address2");
  const physical_city = formContext.watch("physical_city");
  const physical_state = formContext.watch("physical_state");
  const physical_zip = formContext.watch("physical_zip");
  const companyName = formContext.watch("companyName");
  const licenseNumber = formContext.watch("licenseNumber");
  const formValues = {
    firstName,
    lastName,
    email,
    phone,
    address,
    address2,
    city,
    state,
    zip,
    physical_address,
    physical_address2,
    physical_city,
    physical_state,
    physical_zip,
    companyName,
    licenseNumber,
  };

  // Debounce search for citizens
  useEffect(() => {
    if (!canSearchWithFormValues(formValues)) {
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const getData = setTimeout(() => {
      getCitizens(formValues);
    }, 350); // 350 ms seems to be the sweet spot
    return () => clearTimeout(getData);
  }, [
    firstName,
    lastName,
    email,
    phone,
    address,
    address2,
    city,
    state,
    zip,
    physical_address,
    physical_address2,
    physical_city,
    physical_state,
    physical_zip,
    companyName,
    licenseNumber,
  ]);

  // mutation to create contact
  const { run: createContact, loading: isCreateContactLoading } = useApiCallWithToast({
    apiCall: () => postContact(networkHeaders),
    onSuccess: async (res: any) => {
      if (closeModalOnCreate) closeModal();
      const user = await res.json();
      if (!user?.data?.attributes) return;
      onCreateContact(user?.data?.id, personRubyToPermitContactFormData(user.data.attributes));
    },
    successMessage: "New user created successfully",
    useResponseErrorMessage: true,
  });

  // creates user from form field information and goes to applying screen
  const createUserAndBeginApplication = async (data: any) => {
    const payload = buildNewContactPayload(data);
    createContact(payload);
  };

  // closes modal and resets state
  const closeModal = () => {
    setAccounts([]);
    setSelectedApplicantId(undefined);
    setModalClosed();
  };

  const [accounts, setAccounts] = useState<any[]>([]); // citizen accounts matching search query
  return (
    <Modal
      className='select-applicant-modal'
      open={open}
      onClose={closeModal}
      content={
        <Form>
          <FormProvider {...formContext}>
            <Grid columns={2}>
              {/* select applicant column */}
              <Grid.Column className='select-applicant' width={7}>
                <span className='capital-text'>{subHeaderText}</span>
                <Header sub style={{ marginTop: 0 }} content={applicationType} />
                <Header style={{ marginTop: 5, marginBottom: 24 }}>
                  {applicationType ? "Select Applicant" : headerText}
                </Header>
                <ContactFieldsV2
                  autofillCountry={true}
                  physicalAddressPrefix='physical'
                  showMailingAddressHeader={false}
                  visibilityOptions={contactInfoOptions}
                />
                <Controller as={<input type='hidden' />} name={`personSubType`} defaultValue={"citizen"} />
                <Button
                  primary
                  content={createContactButtonText}
                  loading={isCreateContactLoading || isCreatePermitContactLoading}
                  disabled={isCreateContactLoading || isCreatePermitContactLoading}
                  fluid
                  className='tw-mb-8 tw-mt-4'
                  onClick={formContext.handleSubmit(createUserAndBeginApplication)}
                />
              </Grid.Column>

              {/* recommended matches column */}
              <Grid.Column className='recommended-matches' width={9}>
                <div>
                  <Icon name='idea' />
                  <span className='capital-text'>Recommended Matches</span>
                  <Icon className='close-select-applicant-modal' name='close' onClick={() => closeModal()} />
                </div>
                <Divider />

                {/* contact matches */}
                {!searchLoading && searchError && (
                  <Message size='small' warning className='button-message'>
                    <div>
                      <Message.Header>Oops</Message.Header>
                      <Message.Content>Something went wrong. Please try again.</Message.Content>
                    </div>
                    <Button className='button-warning' content='Retry' onClick={() => getCitizens(formValues)} />
                  </Message>
                )}
                {searchLoading && <Loader active>Loading</Loader>}
                {!searchLoading &&
                  accounts.map((contact: PersonBodyES) => {
                    const contactFields = [
                      {
                        text: contact.email,
                        icon: <FontAwesomeIcon icon={faEnvelope} color='dark gray' />,
                      },
                      {
                        text: contact.address,
                        icon: <FontAwesomeIcon icon={faMailboxFlagUp} color='dark gray' />,
                      },
                      {
                        text: contact.physical_address,
                        icon: <FontAwesomeIcon icon={faLocationDot} color='dark gray' />,
                      },
                      {
                        text: parsedPhoneNumberAsString(contact.phone ?? ""),
                        icon: <FontAwesomeIcon icon={faPhone} color='dark gray' />,
                      },
                      {
                        text: contact.companyName,
                        icon: <FontAwesomeIcon icon={faBriefcase} color='dark gray' />,
                      },
                      {
                        text: contact.licenseNumber,
                        icon: <FontAwesomeIcon icon={faIdBadge} color='dark gray' />,
                      },
                    ].filter((field) => field.text); // filter out null/empty results
                    return (
                      <>
                        <Segment
                          className='recommended-matches-card'
                          key={contact.id}
                          onClick={() => setSelectedApplicantId(contact.id)}
                        >
                          <b>
                            <Checkbox
                              radio
                              checked={selectedApplicantId === contact.id}
                              label={`${contact.firstName} ${contact.lastName}`}
                            />
                          </b>
                          <Divider style={{ marginTop: "2px", marginBottom: "10px" }} />
                          <div className='horizontal-flex'>
                            <div className='recommended-matches-flex'>
                              {/* contact info fields */}
                              {contactFields.map((field, index) => {
                                return (
                                  <Popup
                                    key={index}
                                    hoverable
                                    inverted
                                    basic
                                    size='small'
                                    mouseEnterDelay={500}
                                    hideOnScroll
                                    content={field.text}
                                    trigger={
                                      <div
                                        style={{
                                          width: "48%",
                                          alignItems: "center",
                                          display: "flex",
                                          gap: "6px",
                                        }}
                                      >
                                        {field.icon}
                                        <p className='ellipses-overflow'>{field.text}</p>
                                      </div>
                                    }
                                  />
                                );
                              })}
                            </div>
                          </div>
                        </Segment>

                        {/* begin application button */}
                        {selectedApplicantId === contact.id && (
                          <Button
                            color='blue'
                            loading={isSelectContactLoading}
                            disabled={isSelectContactLoading}
                            content={selectContactButtonText}
                            onClick={() => {
                              onSelectContact(contact.id.toString(), personESToPermitContactFormData(contact));
                              if (closeModalOnSelect) closeModal();
                            }}
                          />
                        )}
                      </>
                    );
                  })}
                {accounts.length === 0 && !searchLoading && !searchError && canSearchWithFormValues(formValues) && (
                  <Message size='small' info>
                    <Message.Header>No Matches Found</Message.Header>
                    <Message.Content>We couldn{"'"}t find any matches for the information you entered.</Message.Content>
                  </Message>
                )}
                {accounts.length === 0 && !searchLoading && !searchError && !canSearchWithFormValues(formValues) && (
                  <Message size='small' info>
                    <Message.Header>Start Typing to Search</Message.Header>
                    <Message.Content>As you fill out the form, relavant contacts will appear here.</Message.Content>
                  </Message>
                )}
              </Grid.Column>
            </Grid>
          </FormProvider>
        </Form>
      }
    />
  );
};
